import { FC, useEffect, useRef, useState } from 'react';

import type { PDFDocumentProxy } from 'pdfjs-dist';

const THUMB_WIDTH = 112;

const Thumb: FC<{
  pdf: PDFDocumentProxy;
  pageNumber: number;
  active: boolean;
  suspended: boolean;
  root: HTMLElement | null;
  onSelect: (pageNumber: number) => void;
}> = ({ pdf, pageNumber, active, suspended, root, onSelect }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = buttonRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setVisible(entry.isIntersecting)),
      { root, rootMargin: '400px 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!visible || suspended || !canvas) {
      return undefined;
    }
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<unknown> } | undefined;
    (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) {
        return;
      }
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: THUMB_WIDTH / base.width });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const canvasContext = canvas.getContext('2d');
      if (canvasContext) {
        task = page.render({ canvasContext, viewport });
        await task.promise;
      }
    })().catch(() => undefined);
    return () => {
      cancelled = true;
      task?.cancel();
      canvas.width = 0;
      canvas.height = 0;
    };
  }, [visible, suspended, pdf, pageNumber]);

  useEffect(() => {
    if (active && typeof buttonRef.current?.scrollIntoView === 'function') {
      buttonRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={`Go to page ${pageNumber}`}
      aria-current={active ? 'page' : undefined}
      onClick={() => onSelect(pageNumber)}
      className={`flex w-full flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors ${
        active
          ? 'border-pink-500 bg-gray-900 text-white'
          : 'border-transparent-0 text-gray-400 hover:border-slate-500 hover:bg-gray-900'
      }`}
    >
      <span className="flex min-h-[80px] items-center justify-center bg-white">
        <canvas ref={canvasRef} aria-hidden="true" />
      </span>
      {pageNumber}
    </button>
  );
};

export const Thumbnails: FC<{
  pdf: PDFDocumentProxy;
  pageCount: number;
  currentPage: number;
  suspended: boolean;
  onSelect: (pageNumber: number) => void;
}> = ({ pdf, pageCount, currentPage, suspended, onSelect }) => {
  const [root, setRoot] = useState<HTMLElement | null>(null);

  return (
    <nav
      ref={setRoot}
      aria-label="Page thumbnails"
      className="hidden w-36 shrink-0 flex-col gap-1 overflow-y-auto border-r border-slate-500 p-2 md:flex"
    >
      {Array.from({ length: pageCount }, (_unused, index) => (
        <Thumb
          key={index}
          pdf={pdf}
          pageNumber={index + 1}
          active={currentPage === index + 1}
          suspended={suspended}
          root={root}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
};
